import * as i9 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, ElementRef, Component, Input, HostBinding, ViewChild, ViewChildren, HostListener, EventEmitter, Output, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { DateTime, Info } from 'luxon';
import * as i1$1 from 'se-fe-form-service';
import { SeFeFormControlDirective, SE_FE_FORM_FIELD_INPUT } from 'se-fe-form-service';
import { __awaiter } from 'tslib';
import * as i10 from '@angular/cdk/a11y';
import { FocusKeyManager, A11yModule } from '@angular/cdk/a11y';
import { Subject } from 'rxjs';
import * as i2 from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import * as i4 from 'se-fe-position';
import * as i5 from 'se-fe-form-field-suffix';
import { SeFeFormFieldSuffixModule } from 'se-fe-form-field-suffix';
import * as i1 from 'se-fe-button';
import { SeFeButtonModule } from 'se-fe-button';

const DATE_FORMAT = 'MM/dd/yyyy';
var SeFeCalendarSvgPath;
(function (SeFeCalendarSvgPath) {
    SeFeCalendarSvgPath["range"] = "M19 4h-1V3c0-.55-.45-1-1-1s-1 .45-1 1v1H8V3c0-.55-.45-1-1-1s-1 .45-1 1v1H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 15c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1V9h14v10zM7 11h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z";
    SeFeCalendarSvgPath["today"] = "M19 3h-1V2c0-.55-.45-1-1-1s-1 .45-1 1v1H8V2c0-.55-.45-1-1-1s-1 .45-1 1v1H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6c-.55 0-1-.45-1-1V8h14v10c0 .55-.45 1-1 1zM8 10h3c.55 0 1 .45 1 1v3c0 .55-.45 1-1 1H8c-.55 0-1-.45-1-1v-3c0-.55.45-1 1-1z";
})(SeFeCalendarSvgPath || (SeFeCalendarSvgPath = {}));

function excludedDate(date, exclusions) {
    date = date || '';
    const luxDate = DateTime.fromFormat(date, DATE_FORMAT);
    if (!luxDate.isValid)
        return false;
    const isoDate = luxDate.toISODate();
    for (const ex of exclusions) {
        if (!ex.start && isoDate <= ex.end) {
            return true;
        }
        if (!ex.end && isoDate >= ex.start)
            return true;
        if (isoDate >= ex.start && isoDate <= ex.end)
            return true;
    }
    return false;
}
function dateExcludedValidator(exclusions) {
    return (control) => {
        return excludedDate(control.value, exclusions) ? { dateOutOfRange: true } : null;
    };
}

function validYear(date, years) {
    date = date || '';
    const luxDate = DateTime.fromFormat(date, DATE_FORMAT);
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + (years.future || 0);
    const minYear = currentYear - (years.past || 0);
    if (!luxDate.isValid)
        return false;
    const controlYear = luxDate.year;
    if (controlYear < minYear && controlYear < maxYear)
        return true;
    return false;
}
function dateYearRangeValidator(years) {
    return (control) => {
        return validYear(control.value, years) ? { dateOutOfRange: true } : null;
    };
}

class SeFeDatepickerService {
    constructor() {
        this.date = DateTime.local();
        this.excludedDate = excludedDate;
        this.calendarDays$ = new Subject();
        this.selectedDate$ = new Subject();
    }
    selectedDate(date, exclude) {
        if (this.excludedDate(date.toFormat(DATE_FORMAT), exclude))
            return;
        this.inputDate = this.date = date;
        this.selectedDate$.next(date);
    }
    createCalendar() {
        const firstDay = this.date.startOf('month');
        const { daysInMonth } = this.date;
        const days = Array.from({ length: daysInMonth })
            .map((_, i) => i)
            .map((n) => {
            return firstDay.plus({ days: n });
        });
        const previousMonth = this.date.minus({ month: 1 });
        const startOfWeek = firstDay.weekday === 7 ? 0 : firstDay.weekday;
        for (let n = 0; n < startOfWeek; n++) {
            days.unshift(previousMonth.endOf('month').plus({ days: -n }));
        }
        const nextMonth = this.date.plus({ month: 1 });
        for (let n = 0; n < this.remainingCalendarDays(this.date); n++) {
            days.push(nextMonth.startOf('month').plus({ days: n }));
        }
        this.calendarDays$.next(days);
    }
    remainingCalendarDays(date) {
        const amountofDay = date.daysInMonth + date.startOf('month').weekday;
        const weeksToDisplay = Math.ceil((date.daysInMonth + date.startOf('month').weekday) / 7);
        return weeksToDisplay * 7 - amountofDay;
    }
}
SeFeDatepickerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SeFeDatepickerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class SeFeDatepickerCalendarDayComponent {
    constructor(service) {
        this.service = service;
        this.excludedDate = excludedDate;
        this.validYear = validYear;
        this.exclude = [];
        this.tabindex = false;
        this.role = 'cell';
    }
    focus() {
        this.dayEl.nativeElement.focus();
        this.tabindex = true;
    }
    selectedDate(date) {
        this.service.selectedDate(date, this.exclude);
    }
    isSelected(date) {
        if (!this.service.inputDate)
            return;
        return date.equals(this.service.inputDate);
    }
    index(i) {
        return i;
    }
    onKeyup(event, day) {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.stopPropagation();
            event.preventDefault();
            this.selectedDate(day);
        }
    }
    isToday(date) {
        return date ?
            DateTime.now().toFormat(DATE_FORMAT) === date.toFormat(DATE_FORMAT) :
            false;
    }
    isDifferentMonth(date) {
        const displayedMonth = this.service.date.month;
        return date ?
            displayedMonth !== date.month :
            false;
    }
    disableDate(day) {
        const date = day.toFormat('MM/dd/yyyy');
        return this.excludedDate(date, this.exclude) || this.validYear(date, this.yearRange);
    }
    ngOnInit() {
        this.service.createCalendar();
    }
    get ariaLabelDay() {
        return `${this.day.toFormat('cccc LLLL d yyyy')}`;
    }
}
SeFeDatepickerCalendarDayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarDayComponent, deps: [{ token: SeFeDatepickerService }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarDayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarDayComponent, selector: "se-fe-datepicker-calendar-day", inputs: { exclude: ["seFeDataExclude", "exclude"], yearRange: ["seFeDataYearRange", "yearRange"], day: ["seFeDataDay", "day"], tabindex: "tabindex" }, host: { properties: { "attr.role": "this.role" } }, viewQueries: [{ propertyName: "dayEl", first: true, predicate: ["datepickerDay"], descendants: true, read: ElementRef }], ngImport: i0, template: "\n<div\n  #datepickerDay\n  class=\"se-fe-datepicker__day\"\n  role=\"button\"\n  [ngClass]=\"{\n    'se-fe-theme--interactive': !disableDate(day),\n    'se-fe-theme--can-focus se-fe-theme--can-disable': disableDate(day),\n    'se-fe-datepicker__day--different-month': isDifferentMonth(day),\n    'se-fe-datepicker__day--today': isToday(day),\n    'se-fe-datepicker__day--selected': isSelected(day)\n  }\"\n  [attr.tabindex]=\"tabindex ? '0' : '-1'\"\n  [attr.aria-selected]=\"isSelected(day)\"\n  [attr.aria-disabled]=\"disableDate(day)\"\n  [attr.aria-label]=\"ariaLabelDay\"\n  (click)=\"selectedDate(day)\"\n  (keypress)=\"onKeyup($event, day)\">\n    {{ day.toFormat('d') }}\n</div>\n", styles: [".se-fe-datepicker__day{font-size:14px;display:flex;width:40px;height:40px;justify-content:center;align-items:center;cursor:pointer;border-radius:50%;box-sizing:border-box;padding:2px;outline:0}.se-fe-datepicker__day[aria-disabled=true]{cursor:not-allowed}.se-fe-datepicker__day--today{border:1px solid var(--se-border)}.se-fe-datepicker__day--selected{background:color-mix(in srgb,var(--se-accent) var(--se-pressed-mix),var(--se-bg))}.se-fe-datepicker__day--different-month{--se-bg: var(--se-popup-bg);color:color-mix(in srgb,var(--se-text) 34%,var(--se-bg))}.se-fe-datepicker__day--different-month.se-fe-datepicker__day--selected{background:unset}.se-fe-datepicker__day--different-month.se-fe-datepicker__day--selected:focus{outline:0;box-shadow:none;background:none}\n"], directives: [{ type: i9.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarDayComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar-day',
                    templateUrl: './se-fe-datepicker-calendar-day.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar-day.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeDatepickerService }]; }, propDecorators: { exclude: [{
                type: Input,
                args: ['seFeDataExclude']
            }], yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], day: [{
                type: Input,
                args: ['seFeDataDay']
            }], tabindex: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], dayEl: [{
                type: ViewChild,
                args: ['datepickerDay', { read: ElementRef, static: false }]
            }] } });

class SeFeDatepickerCalendarComponent {
    constructor(service, cdr) {
        this.service = service;
        this.cdr = cdr;
        this.weekdays = [];
        this.days = [];
        this.weeks = [];
        this.exclude = [];
        this.buildWeekdays();
        this.subscription = [
            this.service.calendarDays$.subscribe(days => {
                if (this.keyManager) {
                    setTimeout(() => {
                        this.ngAfterViewInit();
                    });
                }
                this.days = days;
                const weekSize = 7;
                this.weeks = [];
                for (let i = 0; i < this.days.length; i += weekSize) {
                    const chunk = this.days.slice(i, i + weekSize);
                    this.weeks.push(chunk);
                }
            }),
        ];
    }
    manage(event) {
        if (!this.keyManager.activeItem) {
            this.setInitalActiveItem();
            return;
        }
        const lastActiveDay = this.keyManager.activeItem.day;
        const lastActiveItem = this.keyManager.activeItem;
        const formattedLastActiveDay = new Date(`${lastActiveDay.month}/${lastActiveDay.day}/${lastActiveDay.year}`);
        if (event.shiftKey && event.code === 'PageUp') {
            this.service.date = this.service.date.plus({ month: 1 });
            this.service.createCalendar();
        }
        if (event.shiftKey && event.code === 'PageDown') {
            this.service.date = this.service.date.minus({ month: 1 });
            this.service.createCalendar();
        }
        switch (event.code) {
            case 'ArrowDown':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const downArrowIndex = this.keyManager.activeItemIndex + 7;
                const downArrowNewDay = lastActiveDay.day + 7;
                // next month
                if (downArrowNewDay > lastActiveDay.daysInMonth) {
                    const downNewDay = lastActiveDay.day + 7;
                    this.updateCalendar(formattedLastActiveDay, downNewDay);
                    break;
                }
                this.keyManager.setActiveItem(downArrowIndex);
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'ArrowUp':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const upArrowIndex = this.keyManager.activeItemIndex - 7;
                const upArrowNewDay = lastActiveDay.day - 7;
                // previous month
                if (upArrowNewDay <= 0) {
                    const upNewDay = lastActiveDay.day - 7;
                    this.updateCalendar(formattedLastActiveDay, upNewDay);
                    break;
                }
                this.keyManager.setActiveItem(upArrowIndex);
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'ArrowRight':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const rightArrowNewDay = lastActiveDay.day + 1;
                if (rightArrowNewDay > lastActiveDay.daysInMonth) {
                    const rightNewDay = lastActiveDay.day + 1;
                    this.updateCalendar(formattedLastActiveDay, rightNewDay);
                    break;
                }
                this.keyManager.setNextItemActive();
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'ArrowLeft':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const leftArrowNewDay = lastActiveDay.day - 1;
                if (leftArrowNewDay <= 0) {
                    const leftNewDay = lastActiveDay.day - 1;
                    this.updateCalendar(formattedLastActiveDay, leftNewDay);
                    break;
                }
                this.keyManager.setPreviousItemActive();
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'Home':
                event.preventDefault();
                if (lastActiveDay.weekday === 7) {
                    break;
                }
                const homeNewDay = lastActiveDay.day - lastActiveDay.weekday;
                this.updateCalendar(formattedLastActiveDay, homeNewDay);
                lastActiveItem.tabindex = false;
                break;
            case 'End':
                event.preventDefault();
                if (lastActiveDay.weekday === 6) {
                    break;
                }
                const dayAdjust = lastActiveDay.weekday === 7 ? 6 : 6 - lastActiveDay.weekday;
                const endNewDay = lastActiveDay.day + dayAdjust;
                this.updateCalendar(formattedLastActiveDay, endNewDay);
                lastActiveItem.tabindex = false;
                break;
            case 'PageUp':
                event.preventDefault();
                if (event.shiftKey)
                    break;
                this.service.date = this.service.date.minus({ month: 1 });
                this.service.createCalendar();
                break;
            case "PageDown":
                event.preventDefault();
                if (event.shiftKey)
                    break;
                this.service.date = this.service.date.plus({ month: 1 });
                this.service.createCalendar();
                break;
            default:
                break;
        }
    }
    updateCalendar(date, day) {
        return __awaiter(this, void 0, void 0, function* () {
            this.service.date = DateTime.fromJSDate(new Date(date.setDate(day)));
            yield this.service.createCalendar();
            this.cdr.detectChanges();
            this.keyManager.setActiveItem(this.newDateIndex);
        });
    }
    setInitalActiveItem() {
        if (this.keyManager.activeItem)
            return;
        setTimeout(() => {
            this.keyManager.setActiveItem(this.newDateIndex);
        });
    }
    ngOnDestroy() {
        this.subscription.forEach(sub => sub.unsubscribe());
    }
    ngOnInit() {
        this.service.createCalendar();
    }
    ngAfterViewInit() {
        this.keyManager = new FocusKeyManager(this.daysList);
        this.tabindex();
        this.setInitalActiveItem();
    }
    buildWeekdays() {
        const weekdayNum = [0, 1, 2, 3, 4, 5, 6];
        weekdayNum.forEach(d => {
            const luxonDay = (d + 6) % 7;
            this.weekdays.push({ long: Info.weekdays('long')[luxonDay], short: Info.weekdays('short')[luxonDay].slice(0, 2) });
        });
    }
    get newDateIndex() {
        return this.daysList.toArray().findIndex(d => {
            return d.day.day === this.service.date.day && d.day.month === this.service.date.month;
        });
    }
    index(i, day) {
        return day.month;
    }
    tabindex() {
        this.daysList.forEach(d => {
            if (d.tabindex === true && (d.day.day !== this.service.date.day || d.day.month !== this.service.date.month)) {
                d.tabindex = false;
            }
        });
    }
}
SeFeDatepickerCalendarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarComponent, deps: [{ token: SeFeDatepickerService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarComponent, selector: "se-fe-datepicker-calendar", inputs: { exclude: ["seFeDataExclude", "exclude"], yearRange: ["seFeDataYearRange", "yearRange"], id: ["seFeDataTableId", "id"] }, host: { listeners: { "keydown": "manage($event)" } }, viewQueries: [{ propertyName: "daysList", predicate: SeFeDatepickerCalendarDayComponent, descendants: true }], ngImport: i0, template: "\n  <table role=\"grid\" class=\"se-fe-datepicker__table\" tabindex=\"2\">\n    <thead>\n      <tr class=\"se-fe-datepicker__table-row\">\n        <th *ngFor=\"let weekday of weekdays\" scope=\"col\" class=\"se-fe-datepicker__weekday\" [attr.abbr]=\"weekday.long\">\n          {{ weekday.short }}\n        </th>\n      </tr>\n    </thead>\n    <tbody>\n      <tr class=\"se-fe-datepicker__table-row\" *ngFor=\"let week of weeks; let i = index; trackBy: index\">\n          <se-fe-datepicker-calendar-day\n          *ngFor=\"let day of week; let i = index; trackBy: index\"\n          [seFeDataDay]=\"day\"\n          [seFeDataExclude]=\"exclude\"\n          [seFeDataYearRange]=\"yearRange\">\n        </se-fe-datepicker-calendar-day>\n      </tr>\n    </tbody>\n  </table>\n", styles: [".se-fe-datepicker__table{display:flex;flex-direction:column;flex-wrap:wrap;justify-content:space-around;font:var(--se-small-control-font);font-weight:var(--se-bold)}.se-fe-datepicker__table-row{display:flex;flex-direction:row;margin-bottom:var(--se-content-spacing-xsmall);justify-content:space-around;padding:0 3px}.se-fe-datepicker__table-row .se-fe-datepicker__weekday{display:flex;width:44px;height:32px;justify-content:center;align-items:flex-end}\n"], components: [{ type: SeFeDatepickerCalendarDayComponent, selector: "se-fe-datepicker-calendar-day", inputs: ["seFeDataExclude", "seFeDataYearRange", "seFeDataDay", "tabindex"] }], directives: [{ type: i9.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar',
                    templateUrl: './se-fe-datepicker-calendar.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeDatepickerService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { exclude: [{
                type: Input,
                args: ['seFeDataExclude']
            }], yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], id: [{
                type: Input,
                args: ['seFeDataTableId']
            }], daysList: [{
                type: ViewChildren,
                args: [SeFeDatepickerCalendarDayComponent]
            }], manage: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

function invalidDate(date) {
    const luxDate = DateTime.fromFormat(date, DATE_FORMAT);
    return !luxDate.isValid;
}
function dateValidation(control) {
    if (!control.value)
        return null;
    return invalidDate(control.value) ? { dateInvalid: true } : null;
}

class SeFeDatepickerCalendarHeaderComponent {
    constructor(service) {
        this.service = service;
        this.years = [];
        this.months = this.monthsOptions;
        this.selectedMonth = this.service.date.month;
        this.selectedYear = this.service.date.year;
        this.subscription = this.service.calendarDays$.subscribe(() => {
            this.selectedMonth = this.service.date.month;
            this.selectedMonthLong = this.service.date.monthLong;
            this.selectedYear = this.service.date.year;
        });
    }
    ngOnChanges(changes) {
        changes.yearRange ? this.getYears(this.yearRange) : null;
    }
    changeMonth(index) {
        this.service.date = this.service.date.set({ month: index });
        this.selectedMonth = this.service.date.month;
        this.service.createCalendar();
    }
    changeYear(event) {
        this.service.date = this.service.date.set({ year: event });
        this.selectedYear = this.service.date.year;
        this.service.createCalendar();
    }
    nextMonth() {
        this.service.date = this.service.date.plus({ month: 1 });
        this.service.createCalendar();
    }
    previousMonth() {
        this.service.date = this.service.date.minus({ month: 1 });
        this.service.createCalendar();
    }
    getYears(years) {
        const { sort } = years;
        const currentYear = new Date().getFullYear();
        const maxYear = currentYear + (years.future || 0);
        const minYear = currentYear - (years.past || 0);
        for (let y = minYear; y <= maxYear; y++) {
            this.years.push(y);
        }
        if (sort === 'desc') {
            this.years.reverse();
        }
        return this.years;
    }
    get monthsOptions() {
        const monthNames = Info.months();
        return monthNames.map((month, i) => {
            return { name: `${i + 1} ${month}` };
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
SeFeDatepickerCalendarHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarHeaderComponent, deps: [{ token: SeFeDatepickerService }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarHeaderComponent, selector: "se-fe-datepicker-calendar-header", inputs: { yearRange: ["seFeDataYearRange", "yearRange"], id: ["seFeDataHeaderId", "id"] }, usesOnChanges: true, ngImport: i0, template: "<div class=\"se-fe-datepicker__header\">\n  <div class=\"se-fe-datepicker__select-month\">\n    <select class=\"se-fe-theme--form-field\" aria-label=\"Month\" name=\"month\" [(ngModel)]=\"selectedMonth\" (ngModelChange)=\"changeMonth($event)\">\n      <option *ngFor=\"let month of months; let i = index\" [ngValue]=\"i + 1\">{{ month.name }}</option>\n    </select>\n  </div>\n \n  <div class=\"se-fe-datepicker__select-year\">\n    <select class=\"se-fe-theme--form-field\" aria-label=\"Year\" name=\"year\" [(ngModel)]=\"selectedYear\" (ngModelChange)=\"changeYear($event)\">\n      <option *ngFor=\"let year of years\" [value]=\"year\">{{ year }}</option>\n    </select>\n  </div>\n  \n  <div class=\"se-fe-datepicker__month-buttons\">\n    <button class=\"se-fe-theme--interactive\" (click)=\"previousMonth()\" aria-label=\"Previous Month\"><span class=\"se-fe-datepicker__prev-arrow\"></span></button>\n    <button class=\"se-fe-theme--interactive\" (click)=\"nextMonth()\" aria-label=\"Next Month\"><span class=\"se-fe-datepicker__next-arrow\"></span></button>\n  </div>\n</div>\n", styles: [".se-fe-datepicker__prev-arrow:after,.se-fe-datepicker__next-arrow:after,.se-fe-datepicker__header .se-fe-datepicker__select-year:after,.se-fe-datepicker__header .se-fe-datepicker__select-month:after{width:calc(var(--se-rem, 1rem) * .5);height:calc(var(--se-rem, 1rem) * .5)}.se-fe-datepicker__header .se-fe-datepicker__select-year:after,.se-fe-datepicker__header .se-fe-datepicker__select-month:after{cursor:pointer;content:\"\";border:2px solid var(--se-icon);margin:0;display:inline-block;border-left:none;border-bottom:none;position:absolute;right:var(--se-content-spacing-small);top:calc(25% + var(--se-rem, 1rem) * .165);transform:rotate(135deg);pointer-events:none}.se-fe-datepicker__header .se-fe-datepicker__select-year select,.se-fe-datepicker__header .se-fe-datepicker__select-month select{--se-bg: var(--se-popup-bg);cursor:pointer;padding:var(--se-content-spacing-xsmall) var(--se-content-spacing) var(--se-content-spacing-xsmall) var(--se-content-spacing-xsmall);border:1px solid var(--se-border);border-radius:4px;background:var(--se-bg);color:var(--se-text);appearance:none;text-align:left;height:28px;outline:none;font:var(--se-small-control-font);font-weight:var(--se-bold)}.se-fe-datepicker__header{--se-bg: var(--se-popup-bg);background:var(--se-bg);border-bottom:1px solid var(--se-border);display:flex;align-items:center;grid-gap:var(--se-content-spacing-small);gap:var(--se-content-spacing-small);flex-direction:row;padding:var(--se-content-spacing-small)}.se-fe-datepicker__header button{border:1px solid var(--se-border);padding:4px 10px}.se-fe-datepicker__header button:first-of-type{border-bottom-left-radius:4px;border-top-left-radius:4px;border-right:none}.se-fe-datepicker__header button:last-of-type{border-bottom-right-radius:4px;border-top-right-radius:4px}.se-fe-datepicker__header .se-fe-datepicker__select-month{position:relative;margin-right:var(--se-content-spacing-small)}.se-fe-datepicker__header .se-fe-datepicker__select-month select{padding-right:20px;min-width:121px;max-width:142px}.se-fe-datepicker__header .se-fe-datepicker__select-year{position:relative}.se-fe-datepicker__header .se-fe-datepicker__select-year select{width:75px}.se-fe-datepicker__prev-arrow:after,.se-fe-datepicker__next-arrow:after{content:\"\";border:2px solid var(--se-icon);margin:0;display:inline-block;border-left:none;border-bottom:none;vertical-align:middle}.se-fe-datepicker__prev-arrow:after{transform:rotate(225deg);margin-left:calc(var(--se-rem, 1rem) * .3166)}.se-fe-datepicker__next-arrow:after{transform:rotate(45deg);margin-right:calc(var(--se-rem, 1rem) * .3166)}.se-fe-datepicker__month-buttons{display:flex;flex:auto;justify-content:flex-end}\n"], directives: [{ type: i2.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i9.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { type: i2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar-header',
                    templateUrl: './se-fe-datepicker-calendar-header.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar-header.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeDatepickerService }]; }, propDecorators: { yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], id: [{
                type: Input,
                args: ['seFeDataHeaderId']
            }] } });

class SeFeDatepickerCalendarFooterComponent {
    constructor() {
        this.seFeClick = new EventEmitter();
    }
    close() {
        this.seFeClick.emit('close');
    }
    setDate() {
        this.seFeClick.emit('setDate');
    }
}
SeFeDatepickerCalendarFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarFooterComponent, selector: "se-fe-datepicker-calendar-footer", outputs: { seFeClick: "seFeClick" }, ngImport: i0, template: "<se-fe-button [seFeDataSmall]=\"true\" (click)=\"close()\">Cancel</se-fe-button>\n<se-fe-button [seFeDataSmall]=\"true\" [seFeDataEmphasis]=\"'high'\" (click)=\"setDate()\">Ok</se-fe-button>\n", styles: [":host{border-top:1px solid var(--se-border);padding:var(--se-content-spacing-small);display:flex;justify-content:flex-end;grid-gap:var(--se-content-spacing-small);gap:var(--se-content-spacing-small)}\n"], components: [{ type: i1.SeFeButtonComponent, selector: "se-fe-button, se-fe-destructive-button, se-fe-back-button, se-fe-upgrade-button, se-fe-icon-button", inputs: ["seFeDataIsDisabled", "seFeDataFocusableOnDisabled", "seFeDataEmphasis", "seFeDataIsLoading", "seFeDataFullWidth", "seFeDataType", "seFeDataLoadingLabel", "seFeDataIndicatorDot", "seFeDataIndicatorDotColor", "seFeDataButtonAttrs", "seFeDataSnug", "seFeDataSmall"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar-footer',
                    templateUrl: './se-fe-datepicker-calendar-footer.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar-footer.component.scss'],
                }]
        }], propDecorators: { seFeClick: [{
                type: Output
            }] } });

const SELECTORS = 'a, button, area, input, select, textarea, [tabindex]';
class SeFeDatepickerComponent extends SeFeFormControlDirective {
    constructor(el, seFeFormService, renderer, service, fb, cdr, seFePositonService, injector) {
        super(injector, seFeFormService);
        this.el = el;
        this.seFeFormService = seFeFormService;
        this.renderer = renderer;
        this.service = service;
        this.fb = fb;
        this.cdr = cdr;
        this.seFePositonService = seFePositonService;
        this.injector = injector;
        this.baseClass = true;
        this.disabled = false;
        this.visible = false;
        this.name = `datepicker-${SeFeDatepickerComponent.counter}`;
        this.calendarTabindex = true;
        this.errorMessages = {}; // Deprecated
        this.icon = false;
        this.isComfy = true;
        this.suffix = '';
        this.exclude = [];
        this.subscription = [];
        // set IDs
        this.labelId = `se-fe-datepicker-label-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        this.id = `se-fe-datepicker-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        this.errorId = `se-fe-datepicker-error-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        this.helpId = `se-fe-datepicker-help-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        SeFeDatepickerComponent.counter++;
        this.renderer.listen('window', 'click', (e) => {
            var _a, _b, _c, _d;
            // Close the datepicker if the click is either outside the calendar, calendar input, or custom trigger
            const inCalendar = e.composedPath().includes((_a = this.calendar) === null || _a === void 0 ? void 0 : _a.nativeElement);
            const inCalendarInput = e.composedPath().includes((_b = this.calendarInput) === null || _b === void 0 ? void 0 : _b.nativeElement);
            const inCustomTrigger = e.composedPath().includes((_d = (_c = this.customTrigger) === null || _c === void 0 ? void 0 : _c.nativeElement) === null || _d === void 0 ? void 0 : _d.children[0]);
            if (this.visible && !(inCalendar || inCalendarInput || inCustomTrigger)) {
                this.closeCalendar();
            }
        });
        const sub = this.service.selectedDate$
            .subscribe(date => {
            this.onChange(date.toFormat(DATE_FORMAT));
            this.onTouched(date.toFormat(DATE_FORMAT));
            this.closeCalendar();
            this.cdr.markForCheck();
        });
        this.subscription.push(sub);
    }
    get disabledState() { return this.disabled || null; }
    onKeydown(event) {
        if (this.visible) {
            this.closeCalendar();
            event.stopPropagation();
        }
    }
    // expose for the template
    get formControlValue() {
        var _a;
        return (_a = this.formControl) === null || _a === void 0 ? void 0 : _a.value;
    }
    get triggerElement() {
        return this.hasCustomTrigger ? this.customTrigger.nativeElement : this.input.nativeElement;
    }
    get triggerConfig() {
        return {
            name: this.name,
            element: this.triggerElement,
            event: 'none'
        };
    }
    get contentConfig() {
        var _a;
        return {
            name: this.name,
            element: (_a = this.calendar) === null || _a === void 0 ? void 0 : _a.nativeElement,
            position: 'bottom left',
            hide: this.hide.bind(this),
            show: this.show.bind(this)
        };
    }
    hide() {
        this.visible = false;
        this.updateCalendar(); // reset calendar 
        setTimeout(() => this.returnFocus());
        this.cdr.markForCheck();
    }
    show() {
        this.visible = true;
        setTimeout(() => this.calendar.nativeElement.focus()); // force focus into calendar
        this.cdr.markForCheck();
    }
    get hasCustomTrigger() {
        var _a, _b;
        const el = (_a = this.customTrigger) === null || _a === void 0 ? void 0 : _a.nativeElement;
        return !!el && (!!el.childElementCount || !!((_b = el.innerText) === null || _b === void 0 ? void 0 : _b.replace(/\s/g, '')));
    }
    ngAfterViewInit() {
        this.svgPath = SeFeCalendarSvgPath[this.iconPath];
        setTimeout(() => {
            this.seFePositonService.addTrigger(this.triggerConfig);
            this.seFePositonService.addContent(this.contentConfig);
            // Handle form reset
            const sub = this.formControl.valueChanges.subscribe(val => {
                val === null ? this.setValue('') : '';
            });
            this.subscription.push(sub);
            // Handle inital data
            const newDate = DateTime.fromJSDate(new Date(this.formControl.value.replace(/-/g, '/')));
            if (newDate.isValid) {
                this.setValue(newDate.toFormat(DATE_FORMAT));
                this.service.date = newDate;
            }
            this.resetFormValidators();
            this.updateCalendar();
            this.cdr.markForCheck();
        });
    }
    open(trigger = this.defaultTrigger) {
        if (!this.visible) {
            this.trigger = trigger;
        }
        this.toggleCalendar();
    }
    get calendarIconLabel() {
        if (this.formControl.value) {
            const inputDate = DateTime.fromJSDate(new Date(this.formControl.value.replace(/-/g, '/')));
            const weekday = inputDate.weekdayLong;
            const dateFormat = inputDate.toFormat('LLLL d yyyy');
            const str = `Change date, ${weekday} ${dateFormat}`;
            return `${str.slice(0, -5)},${str.slice(-5)}`;
        }
        return 'Choose date';
    }
    handleFooterAction(evt) {
        if (evt === 'setDate')
            this.service.selectedDate(this.service.date, this.exclude);
        this.closeCalendar();
    }
    // support legacy label | help text | errors
    get invalid() {
        return this.formControl ? this.formControl.invalid : false;
    }
    // support legacy label | help text | errors
    get showErrors() {
        if (!this.formControl)
            return false;
        const { touched } = this.formControl;
        return this.invalid ? touched : false;
    }
    // support legacy label | help text | errors
    get controlErrors() {
        if (!this.formControl.touched || !this.formControl.dirty)
            return [];
        const errors = this.formControl.errors || {};
        return Object.keys(errors);
    }
    // support legacy label | help text | errors
    get ariaDesc() {
        if (!this.helpText && !this.errorMessages)
            return null;
        return this.showErrors && this.controlErrors.length ? this.errorId : this.helpId;
    }
    toggleCalendar(event) {
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        this.triggerInstance.toggle();
    }
    closeCalendar() {
        this.triggerInstance.close();
    }
    updateCalendar() {
        this.updateServiceDate();
        this.service.createCalendar();
        this.cdr.markForCheck();
    }
    ngOnDestroy() {
        this.subscription.forEach(sub => sub.unsubscribe());
    }
    change($event) {
        this.formControl.setErrors(null);
        this.onChange($event.currentTarget.value);
        this.cdr.markForCheck();
    }
    touched($event) {
        this.onTouched($event.currentTarget.touched);
        this.cdr.markForCheck();
    }
    writeValue(value) {
        this._value = value;
        this.cdr.markForCheck();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }
    reformatDate($event) {
        if ($event.currentTarget.value) {
            const newDate = DateTime.fromJSDate(new Date($event.currentTarget.value));
            if (newDate.isValid) {
                this.setValue(newDate.toFormat(DATE_FORMAT));
            }
        }
    }
    updateServiceDate() {
        const inputDate = DateTime.fromJSDate(new Date(this.formControl.value));
        const date = inputDate.isValid ? inputDate : DateTime.now();
        this.service.date = date;
        this.service.inputDate = date;
    }
    setValue(value) {
        this.onChange(value);
        this.onTouched(value);
        this.cdr.markForCheck();
    }
    get iconPath() {
        return this.icon ? 'range' : 'today';
    }
    resetFormValidators() {
        if (!this.formControl || !this.formControl.parent)
            return;
        const parent = this.formControl.parent;
        const currentValidators = this.formControl.validator ? [this.formControl.validator] : [];
        const combinedValidators = [
            ...currentValidators,
            dateExcludedValidator(this.exclude),
            dateYearRangeValidator(this.yearRange),
            dateValidation
        ];
        this.formControl.setValidators(combinedValidators);
        this.formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        parent.updateValueAndValidity();
    }
    get defaultTrigger() {
        var _a, _b;
        return ((_a = this.calendarButton) === null || _a === void 0 ? void 0 : _a.nativeElement) || ((_b = this.el) === null || _b === void 0 ? void 0 : _b.nativeElement);
    }
    returnFocus() {
        if (!this.trigger)
            this.trigger = this.defaultTrigger;
        const trigger = this.getTriggerEl(this.trigger);
        if (trigger)
            trigger.focus();
    }
    // TODO: Break these out
    getTriggerEl(trigger) {
        if (this.isString(trigger)) {
            const shadowRootDom = this.el.nativeElement.getRootNode();
            const triggerEl = shadowRootDom.querySelector(trigger);
            return this.focusableEl(triggerEl) || triggerEl;
        }
        if (this.isHTMLElement(trigger)) {
            return this.focusableEl(trigger) || trigger;
        }
        if (this.isEvent(trigger)) {
            const target = trigger.target;
            return this.focusableEl(target) || target;
        }
    }
    focusableEl(el) {
        const selectorsArray = SELECTORS.split(', ');
        const focusable = el && selectorsArray.some(selector => el.matches(selector));
        return focusable ? el.closest(SELECTORS) : el.querySelector(SELECTORS);
    }
    isEvent(input) {
        return input instanceof Event;
    }
    isHTMLElement(input) {
        return input instanceof HTMLElement;
    }
    isString(input) {
        return typeof input === 'string';
    }
    get triggerInstance() {
        return this.seFePositonService.triggers.find(t => t.instance.name === this.name);
    }
}
SeFeDatepickerComponent.counter = 1;
SeFeDatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerComponent, deps: [{ token: i0.ElementRef }, { token: i1$1.SeFeFormService }, { token: i0.Renderer2 }, { token: SeFeDatepickerService }, { token: i2.FormBuilder }, { token: i0.ChangeDetectorRef }, { token: i4.SeFePositionService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerComponent, selector: "se-fe-datepicker", inputs: { label: ["seFeDataLabel", "label"], helpText: ["seFeDataHelpText", "helpText"], errorMessages: ["seFeDataMessages", "errorMessages"], icon: ["seFeDataDateRangeIcon", "icon"], isComfy: ["seFeDataComfy", "isComfy"], suffix: ["seFeDataSuffix", "suffix"], suffixSize: ["seFeDataSuffixSize", "suffixSize"], exclude: ["seFeDataExcludeDates", "exclude"], yearRange: ["seFeDataYearRange", "yearRange"] }, host: { listeners: { "keydown.escape": "onKeydown($event)" }, properties: { "class.se-fe-datepicker": "this.baseClass", "attr.inert": "this.disabledState" } }, providers: [
        SeFeDatepickerService,
        {
            provide: SE_FE_FORM_FIELD_INPUT,
            useExisting: SeFeDatepickerComponent,
        },
    ], viewQueries: [{ propertyName: "customTrigger", first: true, predicate: ["customTrigger"], descendants: true }, { propertyName: "calendar", first: true, predicate: ["calendar"], descendants: true }, { propertyName: "calendarInput", first: true, predicate: ["calendarInput"], descendants: true }, { propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "calendarButton", first: true, predicate: ["calendarButton"], descendants: true }, { propertyName: "calendarDays", first: true, predicate: SeFeDatepickerCalendarComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"se-fe-datepicker__form\">\n  <div #customTrigger><ng-content></ng-content></div>\n  <div #calendarInput *ngIf=\"!hasCustomTrigger\">\n    <label *ngIf=\"label\" [attr.for]=\"labelId\" class=\"se-fe-datepicker__label\">{{ label }}</label>\n    <ng-template #controls>\n      <div\n        #input\n        class=\"se-fe-datepicker__input-field se-fe-theme--form-field\"\n        [ngClass]=\"{'se-fe-datepicker__input-field--comfy': isComfy}\">\n        <input\n          (blur)=\"touched($event)\"\n          (input)=change($event)\n          type=\"text\"\n          [attr.id]=\"labelId\"\n          placeholder=\"mm/dd/yyyy\"\n          class=\"se-fe-theme--form-field-input se-fe-datepicker__input\"\n          [ngClass]=\"{ 'se-fe-datepicker__input--errors': !!controlErrors.length && showErrors }\"\n          (focusin)=\"visible = false\"\n          (focusout)=\"reformatDate($event)\"\n          value=\"{{ formControlValue }}\"\n          [attr.aria-disabled]=\"disabled\"\n          [attr.aria-describedby]=\"ariaDesc\"/>\n        <button #calendarButton type=\"button\" (click)=\"toggleCalendar($event)\" [attr.aria-label]=\"calendarIconLabel\" class=\"se-fe-theme--interactive se-fe-datepicker__button\">\n          <svg class=\"se-fe-datepicker__button-icon\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" height=\"24px\" viewBox=\"0 0 24 24\" width=\"24px\" preserveAspectRatio=\"xMidYMid meet\" aria-hidden=\"true\">\n            <path d=\"M0 0h24v24H0V0z\" fill=\"none\"/>\n            <path stroke-width=\"0\" [attr.d]=\"this.svgPath\"></path>\n          </svg>\n        </button>\n      </div>\n    </ng-template>\n    <se-fe-form-field-suffix\n      *ngIf=\"suffix\"\n      [seFeDataSuffix]=\"suffix\"\n      [seFeDataSize]=\"suffixSize\">\n      <ng-container *ngTemplateOutlet=\"controls\"></ng-container>\n    </se-fe-form-field-suffix>\n    <ng-container *ngIf=\"!suffix\">\n      <ng-container *ngTemplateOutlet=\"controls\"></ng-container>\n    </ng-container>\n\n    <div *ngIf=\"errorMessages && showErrors && controlErrors.length\" [attr.id]=\"errorId\">\n      <div *ngFor=\"let message of errors\" class=\"se-fe-datepicker--error-message\">\n        {{ errorMessages[message] }}\n      </div>\n    </div>\n    <div *ngIf=\"helpText && (!showErrors || !controlErrors.length)\" class=\"se-fe-datepicker__help\" [attr.id]='helpId'>\n      {{ helpText }}\n    </div>\n  </div>\n</div>\n\n<div\n  #calendar\n  role=\"dialog\"\n  aria-modal=\"true\"\n  [attr.inert]=\"visible ? null : 'true'\"\n  aria-label=\"Choose Date\"\n  aria-describedby=\"sr-instructions\"\n  class=\"se-fe-datepicker__calendar\"\n  [ngClass]=\"{\n    'se-fe-datepicker__calendar--custom-trigger': hasCustomTrigger,\n    'se-fe-datepicker__calendar--visible': visible && !disabled\n  }\"\n  (blur)=\"calendarTabindex = false\"\n  [attr.tabindex]=\"visible && calendarTabindex ? '0' : '-1'\"\n  [cdkTrapFocus]=\"visible\">\n  <se-fe-datepicker-calendar-header [seFeDataHeaderId]=\"id\" [seFeDataYearRange]=\"yearRange\"></se-fe-datepicker-calendar-header>\n  <se-fe-datepicker-calendar [seFeDataTableId]=\"id\" [seFeDataExclude]=\"exclude\" [seFeDataYearRange]=\"yearRange\"></se-fe-datepicker-calendar>\n  <se-fe-datepicker-calendar-footer (seFeClick)=\"handleFooterAction($event)\"></se-fe-datepicker-calendar-footer>\n  <div class=\"se-fe-sr-only\" id=\"sr-instructions\" aria-live=\"polite\">Cursor keys can navigate dates</div>\n</div>\n", styles: [".se-fe-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0}.se-fe-sr-only-focusable:focus{position:relative;width:auto;height:auto;clip:initial;margin:inherit;padding:inherit;border:initial}:host-context(se-fe-form-field) .se-fe-datepicker__calendar{margin-top:0}:host{display:block;font:var(--se-body-font);box-sizing:border-box}.se-fe-datepicker{display:flex}.se-fe-datepicker__label{color:var(--se-label);padding-bottom:var(--se-content-spacing-xsmall);display:block}.se-fe-datepicker__input-field{position:relative;border-radius:4px;display:flex;align-items:center}.se-fe-datepicker__input-field:has(.se-fe-datepicker__input--errors){--se-focus-inner: var(--se-critical)}.se-fe-datepicker__input{font:var(--se-body-font);color:var(--se-text);background:var(--se-bg);box-sizing:border-box;padding:var(--se-content-spacing);height:38px;width:100%;border-radius:4px;border:1px solid var(--se-border);outline:none}.se-fe-datepicker__input--errors{border-color:var(--se-critical)}.se-fe-datepicker__help{font:var(--se-caption-font);color:var(--se-caption);margin-top:var(--se-content-spacing-xsmall)}.se-fe-datepicker__button{background:none;border:none;position:absolute;right:var(--se-content-spacing);border-radius:4px;outline:0;cursor:pointer}.se-fe-datepicker__button-icon{fill:var(--se-icon);vertical-align:middle}.se-fe-datepicker__calendar{--se-bg: var(--se-popup-bg);position:absolute;background:var(--se-bg);z-index:-1;opacity:0;flex:1;display:flex;flex-direction:column;width:-moz-fit-content;width:fit-content;justify-content:center;text-align:center;border-radius:4px;box-shadow:var(--se-popup-shadow);pointer-events:none}.se-fe-datepicker__calendar:focus-visible{outline:0;box-shadow:0 0 0 calc(0px + 2px) var(--se-focus-outer),0 0 0 calc(0px + 4px) var(--se-focus-inner),0 0 0 calc(0px + 6px) var(--se-focus-outer)}.se-fe-datepicker__calendar--custom-trigger{margin-top:2px}.se-fe-datepicker__calendar--visible{opacity:1;z-index:var(--se-z-popup);pointer-events:all}.se-fe-datepicker__title{margin:var(--se-content-spacing-small) 0}.se-fe-datepicker--error-message{color:var(--se-critical);font:var(--se-caption-font);margin-top:var(--se-content-spacing-xsmall)}.se-fe-datepicker__input-field--comfy .se-fe-datepicker__input{height:48px}\n"], components: [{ type: i5.SeFeFormFieldSuffixComponent, selector: "se-fe-form-field-suffix", inputs: ["seFeDataSuffix", "seFeDataSize"] }, { type: SeFeDatepickerCalendarHeaderComponent, selector: "se-fe-datepicker-calendar-header", inputs: ["seFeDataYearRange", "seFeDataHeaderId"] }, { type: SeFeDatepickerCalendarComponent, selector: "se-fe-datepicker-calendar", inputs: ["seFeDataExclude", "seFeDataYearRange", "seFeDataTableId"] }, { type: SeFeDatepickerCalendarFooterComponent, selector: "se-fe-datepicker-calendar-footer", outputs: ["seFeClick"] }], directives: [{ type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i9.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i9.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i9.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i10.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker',
                    templateUrl: './se-fe-datepicker.component.html',
                    styleUrls: ['./se-fe-datepicker.component.scss'],
                    providers: [
                        SeFeDatepickerService,
                        {
                            provide: SE_FE_FORM_FIELD_INPUT,
                            useExisting: SeFeDatepickerComponent,
                        },
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1$1.SeFeFormService }, { type: i0.Renderer2 }, { type: SeFeDatepickerService }, { type: i2.FormBuilder }, { type: i0.ChangeDetectorRef }, { type: i4.SeFePositionService }, { type: i0.Injector }]; }, propDecorators: { baseClass: [{
                type: HostBinding,
                args: ['class.se-fe-datepicker']
            }], disabledState: [{
                type: HostBinding,
                args: ['attr.inert']
            }], label: [{
                type: Input,
                args: ['seFeDataLabel']
            }], helpText: [{
                type: Input,
                args: ['seFeDataHelpText']
            }], errorMessages: [{
                type: Input,
                args: ['seFeDataMessages']
            }], icon: [{
                type: Input,
                args: ['seFeDataDateRangeIcon']
            }], isComfy: [{
                type: Input,
                args: ['seFeDataComfy']
            }], suffix: [{
                type: Input,
                args: ['seFeDataSuffix']
            }], suffixSize: [{
                type: Input,
                args: ['seFeDataSuffixSize']
            }], exclude: [{
                type: Input,
                args: ['seFeDataExcludeDates']
            }], yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], customTrigger: [{
                type: ViewChild,
                args: ['customTrigger']
            }], calendar: [{
                type: ViewChild,
                args: ['calendar']
            }], calendarInput: [{
                type: ViewChild,
                args: ['calendarInput']
            }], input: [{
                type: ViewChild,
                args: ['input']
            }], calendarButton: [{
                type: ViewChild,
                args: ['calendarButton']
            }], calendarDays: [{
                type: ViewChild,
                args: [SeFeDatepickerCalendarComponent]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown.escape', ['$event']]
            }] } });

class SeFeDatepickerModule {
}
SeFeDatepickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeDatepickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, declarations: [SeFeDatepickerComponent,
        SeFeDatepickerCalendarHeaderComponent,
        SeFeDatepickerCalendarFooterComponent,
        SeFeDatepickerCalendarComponent,
        SeFeDatepickerCalendarDayComponent], imports: [CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SeFeButtonModule,
        SeFeFormFieldSuffixModule,
        A11yModule], exports: [SeFeDatepickerComponent, ReactiveFormsModule, FormsModule] });
SeFeDatepickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, imports: [[
            CommonModule,
            ReactiveFormsModule,
            FormsModule,
            SeFeButtonModule,
            SeFeFormFieldSuffixModule,
            A11yModule,
        ], ReactiveFormsModule, FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SeFeDatepickerComponent,
                        SeFeDatepickerCalendarHeaderComponent,
                        SeFeDatepickerCalendarFooterComponent,
                        SeFeDatepickerCalendarComponent,
                        SeFeDatepickerCalendarDayComponent,
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        FormsModule,
                        SeFeButtonModule,
                        SeFeFormFieldSuffixModule,
                        A11yModule,
                    ],
                    exports: [SeFeDatepickerComponent, ReactiveFormsModule, FormsModule]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DATE_FORMAT, SeFeCalendarSvgPath, SeFeDatepickerComponent, SeFeDatepickerModule };
